import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import SavedSearchButton from '../components/SavedSearchButton';
import { Filter, Search, X, ChevronDown, ChevronUp, Map as MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { readListingState, saveListingState, patchListingState } from '../utils/listingState';

// Detect mobile viewport once on first render — used to default the filter
// panel to collapsed on phones so the property list is immediately visible
// (matches the Idealista/Kyero mobile UX pattern).
const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < 768;

const PRICE_BUCKETS = [
  { value: 'all', labelNL: 'Alle prijzen', labelEN: 'All prices', labelPL: 'Wszystkie ceny' },
  { value: '0-200000', labelNL: 'Tot €200.000', labelEN: 'Up to €200,000', labelPL: 'Do €200.000' },
  { value: '200000-300000', labelNL: '€200.000 - €300.000', labelEN: '€200,000 - €300,000', labelPL: '€200.000 - €300.000' },
  { value: '300000-500000', labelNL: '€300.000 - €500.000', labelEN: '€300,000 - €500,000', labelPL: '€300.000 - €500.000' },
  { value: '500000-999999999', labelNL: '€500.000+', labelEN: '€500,000+', labelPL: '€500.000+' },
];

const TYPE_OPTIONS = [
  'Appartement', 'Penthouse', 'Villa', 'Geschakelde woning', 'Bungalow',
];

const FEATURE_OPTIONS = [
  { key: 'pool', nl: 'Zwembad', en: 'Pool', pl: 'Basen' },
  { key: 'sea_view', nl: 'Zeezicht', en: 'Sea view', pl: 'Widok na morze' },
  { key: 'near_beach', nl: 'Dicht bij strand', en: 'Near beach', pl: 'Blisko plaży' },
  { key: 'new_build', nl: 'Nieuwbouw', en: 'New build', pl: 'Nowe budownictwo' },
  { key: 'solarium', nl: 'Solarium', en: 'Solarium', pl: 'Solarium' },
  { key: 'garage', nl: 'Garage', en: 'Garage', pl: 'Garaż' },
  { key: 'garden', nl: 'Tuin', en: 'Garden', pl: 'Ogród' },
  { key: 'golf', nl: 'Golfgebied', en: 'Golf area', pl: 'Okolica golfowa' },
];

const FEATURE_KEYWORDS = {
  pool: ['pool', 'zwembad', 'piscina'],
  sea_view: ['sea view', 'zeezicht', 'vistas al mar', 'sea views'],
  near_beach: ['beach', 'strand', 'playa'],
  solarium: ['solarium', 'roof terrace', 'dakterras'],
  garage: ['garage', 'parking', 'carport'],
  garden: ['garden', 'tuin', 'jardin', 'jardín'],
  golf: ['golf'],
};

const hasFeature = (prop, key) => {
  if (key === 'new_build') return !!prop.new_build;
  if (key === 'pool' && prop.pool) return true;
  const hay = (
    (prop.features || []).join(' ') + ' ' +
    (prop.title || '') + ' ' +
    (prop.description || '')
  ).toLowerCase();
  return (FEATURE_KEYWORDS[key] || []).some(kw => hay.includes(kw));
};

const Properties = () => {
  /* Hydrate from sessionStorage on very first render so returning visitors
     land in the same filter/scroll state they left. */
  const persisted = useRef(readListingState());
  const [filters, setFilters] = useState(persisted.current?.filters || {
    region: 'all',
    type: 'all',
    location: 'all',
    price: 'all',
    minBeds: 0,
    minBaths: 0,
    features: [],
  });
  const [sortBy, setSortBy] = useState(persisted.current?.sortBy || 'newest');
  const [searchQuery, setSearchQuery] = useState(persisted.current?.searchQuery || '');
  const [filtersOpen, setFiltersOpen] = useState(
    persisted.current ? !!persisted.current.filtersOpen : !isMobileViewport()
  );
  const { language, t } = useLanguage();
  // Trilingual picker: falls back to NL when a PL string wasn't provided
  // yet, so this never leaves the UI half-empty during rollouts.
  const pick = (nl, en, pl) => (language === 'pl' ? (pl ?? nl) : language === 'en' ? en : nl);

  /* Session-cache the fetched property list per language so back-navigation
     from a detail page and language switches feel instant. The Nelemans
     XML feed is stable within a browsing session; a 5-minute TTL is a
     safe balance between freshness and UX. Cache is intentionally
     sessionStorage (not localStorage) so a browser restart re-fetches. */
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const cacheKey = `casaProperties:v1:${language}`;

  const [properties, setProperties] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.data)) return [];
      if (Date.now() - (parsed.ts || 0) > CACHE_TTL_MS) return [];
      return parsed.data;
    } catch (_) { return []; }
  });
  // Skip the loading spinner if we hydrated from cache — visitor sees the
  // list instantly and we silently revalidate below.
  const [loading, setLoading] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      const parsed = raw ? JSON.parse(raw) : null;
      const fresh = parsed && Array.isArray(parsed.data)
        && Date.now() - (parsed.ts || 0) <= CACHE_TTL_MS;
      return !fresh;
    } catch (_) { return true; }
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/xml/properties?limit=500&lang=${language}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Guard: server may respond with an error object under load;
        // only overwrite state when we actually get an array.
        if (cancelled) return;
        if (!Array.isArray(data)) {
          console.error('Unexpected /api/xml/properties payload', data);
          setLoading(false);
          return;
        }
        setProperties(data);
        setLoading(false);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
        } catch (_) { /* quota exceeded etc. — ignore */ }
      })
      .catch(err => {
        if (cancelled) return;
        console.error(err);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [language, cacheKey]);

  /* Persist filter/sort/search state whenever the visitor changes anything.
     We deliberately do NOT include the filtered id list here — that's derived
     from `properties` which arrives async; we handle it in a separate effect
     once the data is loaded. */
  useEffect(() => {
    patchListingState({ filters, sortBy, searchQuery, filtersOpen });
  }, [filters, sortBy, searchQuery, filtersOpen]);

  /* Restore scroll position after the property list has actually rendered.
     Lazy-loaded images can still be settling their heights when the effect
     first fires, meaning window.scrollTo(2500) would clamp to a much smaller
     document height. We keep polling until either (a) we hit the target
     within 4 px OR (b) the document is tall enough for the target but the
     actual scroll still refuses (target is unreachable — user removed
     content). Ref-flag pauses the scroll-listener below so it doesn't
     overwrite the pending target with intermediate clamped values.

     restoringRef is initialised to `true` when we have a pending scrollY to
     restore — this is critical, because otherwise the scroll listener would
     immediately catch the browser's post-navigation `scrollY=0` scroll event
     and patch it into sessionStorage before this effect gets a chance to
     read the persisted target. */
  const restoringRef = useRef(
    !!(persisted.current?.scrollY && persisted.current.scrollY > 0)
  );
  useEffect(() => {
    if (loading) return;
    const targetY = persisted.current?.scrollY;
    if (typeof targetY !== 'number' || targetY <= 0) {
      restoringRef.current = false;
      return;
    }

    restoringRef.current = true;
    let cancelled = false;
    let elapsed = 0;
    const maxMs = 8000; // Allow up to 8 s for lazy images to expand the layout.
    const stepMs = 100;

    const tryScroll = () => {
      if (cancelled) return;
      window.scrollTo({ top: targetY, behavior: 'instant' });
      const actual = window.scrollY;
      const docTallEnough = document.documentElement.scrollHeight >= targetY + window.innerHeight - 4;
      const closeEnough = Math.abs(actual - targetY) <= 4;
      elapsed += stepMs;
      if (closeEnough || (docTallEnough && actual < targetY - 4) || elapsed >= maxMs) {
        // Give the browser one more frame to settle, then re-enable scroll tracking.
        requestAnimationFrame(() => { restoringRef.current = false; });
        return;
      }
      setTimeout(tryScroll, stepMs);
    };
    requestAnimationFrame(tryScroll);
    return () => { cancelled = true; restoringRef.current = false; };
  }, [loading]);

  /* Continuously track scroll so `sessionStorage` reflects the last-known
     position. Throttled to ~10 writes/sec. Ignored while the restore effect
     above is actively repositioning us — otherwise scrollTo-generated events
     would overwrite the target with transient clamped values while lazy
     images are still expanding the layout. */
  useEffect(() => {
    let scheduledTimer = null;
    const flush = () => {
      scheduledTimer = null;
      if (restoringRef.current) return;
      patchListingState({ scrollY: window.scrollY });
    };
    const onScroll = () => {
      if (scheduledTimer) return;
      scheduledTimer = setTimeout(flush, 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      // Cancel any pending throttled write — it would fire AFTER unmount
      // (when the browser has already clamp-scrolled the window to the new,
      // much shorter detail-page height) and clobber the correct position.
      if (scheduledTimer) {
        clearTimeout(scheduledTimer);
        scheduledTimer = null;
      }
    };
  }, []);

  /* Capture-phase click handler on the grid: saves the exact scroll position
     the instant the visitor clicks a property card, BEFORE React Router
     navigates and BEFORE the DOM swap causes the browser to auto-clamp
     window.scrollY to the new (shorter) document height. This is the only
     reliable moment to snapshot the true scroll position — any later
     capture (throttled listener, unmount cleanup) risks catching a
     clamped-down value. */
  const handleGridClickCapture = () => {
    if (restoringRef.current) return;
    patchListingState({ scrollY: window.scrollY });
  };

  // Dynamic city list from data
  const cityOptions = useMemo(() => {
    const set = new Set(properties.map(p => p.location).filter(Boolean));
    return Array.from(set).sort();
  }, [properties]);

  const filtered = useMemo(() => {
    let list = [...properties];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.location || '').toLowerCase().includes(q) ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.region || '').toLowerCase().includes(q) ||
        (p.ref || '').toLowerCase().includes(q) ||
        (p.id || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }
    if (filters.region !== 'all') list = list.filter(p => p.region === filters.region);
    if (filters.location !== 'all') list = list.filter(p => p.location === filters.location);
    if (filters.type !== 'all') list = list.filter(p => p.property_type === filters.type);
    if (filters.minBeds > 0) list = list.filter(p => (p.bedrooms || 0) >= filters.minBeds);
    if (filters.minBaths > 0) list = list.filter(p => (p.bathrooms || 0) >= filters.minBaths);
    if (filters.price !== 'all') {
      const [min, max] = filters.price.split('-').map(Number);
      list = list.filter(p => p.price >= min && (max ? p.price <= max : true));
    }
    if (filters.features.length > 0) {
      list = list.filter(p => filters.features.every(f => hasFeature(p, f)));
    }

    switch (sortBy) {
      case 'newest':
        list.sort((a, b) => (b.date_listed || '').localeCompare(a.date_listed || ''));
        break;
      case 'oldest':
        list.sort((a, b) => (a.date_listed || '').localeCompare(b.date_listed || ''));
        break;
      case 'price-asc': list.sort((a, b) => a.price - b.price); break;
      case 'price-desc': list.sort((a, b) => b.price - a.price); break;
      case 'bedrooms-desc': list.sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0)); break;
      case 'photos-desc': list.sort((a, b) => (b.images?.length || 0) - (a.images?.length || 0)); break;
      default: break;
    }
    return list;
  }, [properties, searchQuery, filters, sortBy]);

  /* Persist the ordered id list so PropertyDetail can compute prev/next
     without re-fetching. Runs whenever the filtered set changes. */
  useEffect(() => {
    if (loading) return;
    patchListingState({ filteredIds: filtered.map(p => p.id) });
  }, [filtered, loading]);

  const toggleFeature = (key) => {
    setFilters(f => ({
      ...f,
      features: f.features.includes(key)
        ? f.features.filter(k => k !== key)
        : [...f.features, key],
    }));
  };

  const reset = () => {
    setFilters({ region: 'all', type: 'all', location: 'all', price: 'all', minBeds: 0, minBaths: 0, features: [] });
    setSortBy('newest');
    setSearchQuery('');
    // Also reset scroll position so the wipe feels clean.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    saveListingState({
      filters: { region: 'all', type: 'all', location: 'all', price: 'all', minBeds: 0, minBaths: 0, features: [] },
      sortBy: 'newest', searchQuery: '', filtersOpen, scrollY: 0, filteredIds: [],
    });
  };

  const activeCount =
    (filters.region !== 'all' ? 1 : 0) +
    (filters.type !== 'all' ? 1 : 0) +
    (filters.location !== 'all' ? 1 : 0) +
    (filters.price !== 'all' ? 1 : 0) +
    (filters.minBeds > 0 ? 1 : 0) +
    (filters.minBaths > 0 ? 1 : 0) +
    filters.features.length +
    (searchQuery.trim() ? 1 : 0);

  /* If the persisted filter set yields 0 results after the data has loaded,
     auto-expand the filter panel so the visitor immediately sees which filters
     are causing the mismatch. Only fires ONCE per data-load transition — we
     don't want to yank the panel open every time the user manually collapses
     it while browsing 0 results. */
  const [autoOpenedForEmpty, setAutoOpenedForEmpty] = useState(false);
  useEffect(() => {
    if (loading) return;
    if (filtered.length === 0 && activeCount > 0 && !filtersOpen && !autoOpenedForEmpty) {
      setFiltersOpen(true);
      setAutoOpenedForEmpty(true);
    }
    if (filtered.length > 0) setAutoOpenedForEmpty(false);
  }, [loading, filtered.length, activeCount, filtersOpen, autoOpenedForEmpty]);

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.propertiesTitle" descriptionKey="seo.propertiesDescription" />
      <PageHero
        image="https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1920&q=80"
        title={t('properties.title')}
        subtitle={t('properties.subtitle')}
        testIdTitle="properties-page-title"
      />

      <div style={styles.container}>
        {/* Prominent "Search on map" CTA — sits above the filter toggle so the
            map remains a first-class entry point for property discovery. */}
        <Link to="/kaart" style={styles.mapCta} data-testid="properties-map-cta">
          <MapIcon size={18} />
          <span>{pick('Zoek woningen op de kaart', 'Search properties on the map', 'Szukaj nieruchomości na mapie')}</span>
        </Link>

        {/* Save this search + push notifications — the Google Play "real
            native value-add" feature. Always visible on push-capable
            browsers; the button label switches to "Alerts active" once the
            visitor is already subscribed. */}
        <div style={styles.saveRow}>
          <SavedSearchButton
            filters={{ ...filters, searchQuery, sortBy }}
            activeCount={activeCount}
          />
        </div>

        {/* Prominent no-results alert. Appears when persisted or active
            filters yield zero properties — makes the "clear filters" path
            obvious without the visitor having to scroll to the empty state
            at the bottom of the list. */}
        {!loading && filtered.length === 0 && activeCount > 0 && (
          <div style={styles.noResultsBanner} data-testid="no-results-banner">
            <div style={styles.noResultsCopy}>
              <strong style={styles.noResultsTitle}>
                {`Nie znaleziono nieruchomości dla ${activeCount} aktywnych filtrów`}
              </strong>
              <span style={styles.noResultsSub}>
                {`Twój poprzedni wybór nie daje wyników. Wyczyść filtry, aby zobaczyć wszystkie ${properties.length} nieruchomości.`}
              </span>
            </div>
            <button
              type="button"
              onClick={reset}
              style={styles.noResultsReset}
              data-testid="no-results-reset"
            >
              {pick('Wis alle filters', 'Clear all filters', 'Wyczyść wszystkie filtry')}
            </button>
          </div>
        )}

        {/* Compact toggle bar — always visible. Shows filter open/close state,
            active filter count, and the live results count so users see what
            they're working with even when the panel is collapsed (Idealista-style). */}
        <button
          type="button"
          onClick={() => setFiltersOpen(o => !o)}
          style={styles.filterToggle}
          data-testid="toggle-filters-button"
          aria-expanded={filtersOpen}
        >
          <span style={styles.filterToggleLeft}>
            <Search size={18} color="#C5A059" />
            <span style={styles.filterToggleLabel}>
              {pick('Zoek en filter woningen', 'Search and filter properties', 'Szukaj i filtruj nieruchomości')}
            </span>
            {activeCount > 0 && (
              <span style={styles.activeBadge} data-testid="active-filter-count">
                {activeCount}
              </span>
            )}
          </span>
          <span style={styles.filterToggleRight}>
            {!loading && (
              <span style={styles.toggleResultsCount} data-testid="toggle-results-count">
                {filtered.length} {filtered.length === 1
                  ? (pick('woning', 'property', 'nieruchomość'))
                  : (pick('woningen', 'properties', 'nieruchomości'))}
              </span>
            )}
            {filtersOpen ? <ChevronUp size={18} color="#585755" /> : <ChevronDown size={18} color="#585755" />}
          </span>
        </button>

        {filtersOpen && (
          <div style={styles.filterCard} data-testid="filter-panel">
            <div style={styles.filterHeader}>
              <div style={styles.filterHeaderLeft}>
                <Filter size={20} color="#C5A059" />
                <span style={styles.filterTitle}>
                  {pick('Filters', 'Filters', 'Filtry')}
                  {activeCount > 0 && <span style={styles.activeBadge}>{activeCount}</span>}
                </span>
              </div>
              {activeCount > 0 && (
                <button onClick={reset} style={styles.clearAllBtn} data-testid="reset-filters-button">
                  <X size={14} /> {pick('Wis alles', 'Clear all', 'Wyczyść')}
                </button>
              )}
            </div>

            <div style={styles.searchBar}>
              <Search size={18} color="#8C8B88" />
              <input
                type="text"
                placeholder="Szukaj po mieście, numerze referencyjnym lub nazwie projektu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
                data-testid="search-input"
              />
            </div>

            <div style={styles.filters}>
              <Select
                label={pick('Regio', 'Region', 'Region')}
                value={filters.region}
                onChange={(v) => setFilters({ ...filters, region: v })}
                testId="region-filter"
                options={[
                  { v: 'all', l: pick('Alle regio\'s', 'All regions', 'Wszystkie regiony') },
                  { v: 'Costa Blanca', l: 'Costa Blanca' },
                  { v: 'Costa Calida', l: 'Costa Calida' },
                  { v: 'Costa Almeria', l: 'Costa Almeria' },
                ]}
              />
              <Select
                label={pick('Locatie', 'Location', 'Lokalizacja')}
                value={filters.location}
                onChange={(v) => setFilters({ ...filters, location: v })}
                testId="location-filter"
                options={[{ v: 'all', l: pick('Alle steden', 'All cities', 'Wszystkie miasta') }, ...cityOptions.map(c => ({ v: c, l: c }))]}
              />
              <Select
                label={pick('Type', 'Type', 'Typ')}
                value={filters.type}
                onChange={(v) => setFilters({ ...filters, type: v })}
                testId="type-filter"
                options={[{ v: 'all', l: pick('Alle types', 'All types', 'Wszystkie typy') }, ...TYPE_OPTIONS.map(t => ({ v: t, l: t }))]}
              />
              <Select
                label={pick('Prijs', 'Price', 'Cena')}
                value={filters.price}
                onChange={(v) => setFilters({ ...filters, price: v })}
                testId="price-filter"
                options={PRICE_BUCKETS.map(b => ({ v: b.value, l: pick(b.labelNL, b.labelEN, b.labelPL) }))}
              />
              <Select
                label={pick('Slaapkamers', 'Bedrooms', 'Sypialnie')}
                value={String(filters.minBeds)}
                onChange={(v) => setFilters({ ...filters, minBeds: Number(v) })}
                testId="beds-filter"
                options={[
                  { v: '0', l: pick('Alle', 'Any', 'Dowolne') },
                  { v: '1', l: '1+' }, { v: '2', l: '2+' }, { v: '3', l: '3+' }, { v: '4', l: '4+' },
                ]}
              />
              <Select
                label={pick('Badkamers', 'Bathrooms', 'Łazienki')}
                value={String(filters.minBaths)}
                onChange={(v) => setFilters({ ...filters, minBaths: Number(v) })}
                testId="baths-filter"
                options={[
                  { v: '0', l: pick('Alle', 'Any', 'Dowolne') },
                  { v: '1', l: '1+' }, { v: '2', l: '2+' }, { v: '3', l: '3+' },
                ]}
              />
              <Select
                label={pick('Sorteren', 'Sort by', 'Sortuj')}
                value={sortBy}
                onChange={setSortBy}
                testId="sort-filter"
                options={[
                  { v: 'newest', l: pick('Nieuwste eerst', 'Newest first', 'Najnowsze') },
                  { v: 'oldest', l: pick('Oudste eerst', 'Oldest first', 'Najstarsze') },
                  { v: 'price-asc', l: pick('Laagste prijs', 'Lowest price', 'Najniższa cena') },
                  { v: 'price-desc', l: pick('Hoogste prijs', 'Highest price', 'Najwyższa cena') },
                  { v: 'bedrooms-desc', l: pick('Meeste slaapkamers', 'Most bedrooms', 'Najwięcej sypialni') },
                  { v: 'photos-desc', l: pick('Populairst', 'Most popular', 'Najpopularniejsze') },
                ]}
              />
            </div>

            <div style={styles.features}>
              <span style={styles.featuresLabel}>
                {pick('Extra kenmerken', 'Extra features', 'Dodatkowe cechy')}:
              </span>
              <div style={styles.chipRow}>
                {FEATURE_OPTIONS.map(opt => {
                  const active = filters.features.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => toggleFeature(opt.key)}
                      style={active ? styles.chipActive : styles.chip}
                      data-testid={`feature-${opt.key}`}
                    >
                      {pick(opt.nl, opt.en, opt.pl)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Apply / close action — primary on mobile, lets the user collapse
                the panel after they've made their choices and jump straight to
                the results list. */}
            <div style={styles.filterApplyBar}>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                style={styles.applyBtn}
                disabled={loading}
                data-testid="apply-filters-button"
              >
                {loading
                  ? (pick('Bezig met laden...', 'Loading...', 'Ładowanie...'))
                  : `Pokaż ${filtered.length} ${filtered.length === 1 ? 'nieruchomość' : 'nieruchomości'}`}
              </button>
            </div>
          </div>
        )}

        <div style={styles.resultsSection}>
          {!loading && (
            <p style={styles.resultsCount} data-testid="results-count">
              {filtered.length} {filtered.length === 1
                ? (pick('woning gevonden', 'property found', 'nieruchomość znaleziona'))
                : (pick('woningen gevonden', 'properties found', 'nieruchomości znalezionych'))}
            </p>
          )}

          {loading ? (
            <p style={styles.loadingText}>{t('properties.loading')}</p>
          ) : filtered.length > 0 ? (
            <div style={styles.grid} onClickCapture={handleGridClickCapture}>
              {filtered.map((p, idx) => <PropertyCard key={p.id} property={p} priority={idx < 3} />)}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>
                Nie znaleziono nieruchomości spełniających te kryteria.
              </p>
              <button style={styles.resetButton} onClick={reset}>
                {pick('Wis filters', 'Clear filters', 'Wyczyść filtry')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Select = ({ label, value, onChange, options, testId }) => (
  <div style={styles.filterGroup}>
    <label style={styles.label}>{label}</label>
    <select
      style={styles.select}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    >
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const styles = {
  page: { minHeight: '100vh', paddingBottom: '4rem' },
  header: { backgroundColor: '#E6DCCA', padding: '4rem 0', marginBottom: '3rem' },
  container: { maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    color: '#1A1918', marginBottom: '1rem', textAlign: 'center',
  },
  subtitle: { color: '#585755', fontSize: '1.1rem', textAlign: 'center' },
  mapCta: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.6rem',
    width: '100%',
    padding: '0.85rem 1.25rem',
    marginBottom: '0.85rem',
    backgroundColor: '#1A1918',
    color: '#C5A059',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontFamily: "'Manrope', sans-serif",
    border: '1px solid #1A1918',
    transition: 'background 0.2s ease',
  },
  saveRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.85rem',
  },
  noResultsBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
    padding: '1rem 1.25rem',
    marginBottom: '0.85rem',
    backgroundColor: '#FFF4E5',
    borderLeft: '4px solid #E89A3F',
    border: '1px solid #F1D8B0',
    fontFamily: "'Manrope', sans-serif",
  },
  noResultsCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    color: '#1A1918',
    minWidth: 0,
  },
  noResultsTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  noResultsSub: {
    fontSize: '0.82rem',
    color: '#585755',
    lineHeight: 1.35,
  },
  noResultsReset: {
    backgroundColor: '#C5A059',
    color: '#1A1918',
    border: 'none',
    padding: '0.7rem 1.25rem',
    fontSize: '0.8rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flexShrink: 0,
  },
  filterToggle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    padding: '0.95rem 1.25rem',
    marginBottom: '0.85rem',
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
    color: '#1A1918',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  },
  filterToggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    minWidth: 0,
  },
  filterToggleLabel: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#1A1918',
  },
  filterToggleRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexShrink: 0,
  },
  toggleResultsCount: {
    color: '#585755',
    fontSize: '0.82rem',
    fontWeight: 500,
  },
  filterApplyBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '1.25rem',
    marginTop: '0.5rem',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#EAE5DD',
  },
  applyBtn: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '0.9rem 1.75rem',
    fontSize: '0.85rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: "'Manrope', sans-serif",
    width: '100%',
    maxWidth: '320px',
  },
  filterCard: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '1.75rem',
    marginBottom: '2rem',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  filterHeaderLeft: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  filterTitle: { color: '#1A1918', fontSize: '1rem', fontWeight: 600 },
  activeBadge: {
    backgroundColor: '#C5A059',
    color: 'white',
    fontSize: '0.7rem',
    padding: '0.15rem 0.5rem',
    marginLeft: '0.5rem',
    borderRadius: '999px',
  },
  clearAllBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    backgroundColor: 'transparent',
    border: '1px solid #EAE5DD',
    color: '#585755',
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    padding: '0.85rem 1rem',
    marginBottom: '1.25rem',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    fontSize: '0.95rem',
    fontFamily: "'Manrope', sans-serif",
    color: '#1A1918',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '0.85rem',
    marginBottom: '1.25rem',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { color: '#8C8B88', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  select: {
    padding: '0.7rem 0.85rem',
    border: '1px solid #EAE5DD',
    backgroundColor: '#FAF9F6',
    color: '#1A1918',
    fontSize: '0.9rem',
    fontFamily: "'Manrope', sans-serif",
    outline: 'none',
    width: '100%',
  },
  features: {
    paddingTop: '1.25rem',
    borderTop: '1px solid #EAE5DD',
  },
  featuresLabel: {
    color: '#8C8B88',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    display: 'block',
    marginBottom: '0.75rem',
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  chip: {
    backgroundColor: '#FAF9F6',
    color: '#585755',
    border: '1px solid #EAE5DD',
    padding: '0.5rem 1rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  chipActive: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: '1px solid #C5A059',
    padding: '0.5rem 1rem',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  resultsSection: { marginTop: '0.5rem' },
  resultsCount: { color: '#585755', fontSize: '0.95rem', marginBottom: '1.5rem' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '2rem',
  },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
  emptyState: { textAlign: 'center', padding: '4rem 0' },
  emptyText: { color: '#8C8B88', fontSize: '1.1rem', marginBottom: '1.5rem' },
  resetButton: {
    backgroundColor: '#C5A059',
    color: 'white',
    border: 'none',
    padding: '0.85rem 1.5rem',
    fontSize: '0.875rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontWeight: 500,
  },
};

export default Properties;
